import type {
  CollectionReference,
  PartialWithFieldValue,
  WriteBatch,
} from 'firebase/firestore'
import {
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { StreamInterface } from 'tricklejs/dist/types'
import { addDocWithError, getDocsWithError } from '../../firestore-mobx/fetch'
import {
  collectionSnapshots,
  convertDocumentSnapshotToModel,
} from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SectionAssignmentConversationItemPromptTest } from '../../models/SectionAssignmentConversationItemPromptTest'
import type { FirestoreSectionAssignmentConversationItemPromptTest } from './schema'
import { schema, writeSchema } from './schema'
import { type PromptType } from '../PromptVersion/types'
import { type PostAssignmentPromptType } from '../PromptPostAssignmentType/types'

const converter: FirestoreDataConverter<FirestoreSectionAssignmentConversationItemPromptTest> =
  {
    toFirestore: (
      data: PartialWithFieldValue<FirestoreSectionAssignmentConversationItemPromptTest>
    ) => {
      writeSchema.partial().parse(data)
      return data
    },
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      const data = snapshot.data({ serverTimestamps: 'estimate' })
      return schema.parse(data)
    },
  }

const getColRef = (
  firestore: Firestore,
  {
    promptType,
    subType,
    promptVersionId,
    conversationId,
  }: {
    promptType: PromptType.postAssignment
    subType: PostAssignmentPromptType.transcriptSearch
    promptVersionId: string
    conversationId: string
  }
): CollectionReference<FirestoreSectionAssignmentConversationItemPromptTest> => {
  return collection(
    firestore,
    'prompt',
    promptType,
    'type',
    subType,
    'prompt_version',
    promptVersionId,
    'test',
    conversationId,
    'conversation_item'
  ).withConverter(converter)
}

export const getPromptTestConversationItems = (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1]
): StreamInterface<SectionAssignmentConversationItemPromptTest[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  return collectionSnapshots(conversationsQuery).map((snapshot) => {
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(
        repository,
        doc,
        SectionAssignmentConversationItemPromptTest
      )
    })
    return models
  })
}

export const fetchPromptTestConversationItems = async (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1]
): Promise<SectionAssignmentConversationItemPromptTest[]> => {
  const ref = getColRef(repository.firestore, params)

  const orderByCreatedAt = orderBy('createdAt', 'desc')
  const conversationsQuery = query(ref, orderByCreatedAt)

  const snapshot = await getDocsWithError(
    conversationsQuery,
    'FetchPromptTestConversationItemsError'
  )
  const models = snapshot.docs.map((doc) => {
    return convertDocumentSnapshotToModel(
      repository,
      doc,
      SectionAssignmentConversationItemPromptTest
    )
  })
  return models
}

export const createPromptTestConversationItem = async (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & {
    sectionId: string
    assignmentId: string
    userId: string
    role: 'user' // only user is supported from the client side
    userContent: string
  }
): Promise<string> => {
  const ref = getColRef(repository.firestore, params)

  const data = {
    promptVersionId: params.promptVersionId,
    sectionId: params.sectionId,
    assignmentId: params.assignmentId,
    conversationId: params.conversationId,
    userId: params.userId,
    role: params.role,
    userContent: params.userContent,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }

  const doc = await addDocWithError(ref, data, 'CreateConversationItemError')
  return doc.id
}

export const batchDeletePromptTestConversationItem = async (
  repository: FirebaseRepository,
  params: Parameters<typeof getColRef>[1] & {
    batch: WriteBatch
    conversationItemId: string
  }
) => {
  const docRef = doc(
    getColRef(repository.firestore, params),
    params.conversationItemId
  )
  return params.batch.delete(docRef)
}
